import type { Script } from 'c15t';
import { mixpanelAnalytics } from '@c15t/integrations/mixpanel-analytics';
import { redditPixel } from '@c15t/integrations/reddit-pixel';
import { buildGoogleConsentScripts } from '@belong/common';
import { isProd } from 'src/utils/is-prod';
import { getConsentSnapshot } from './consent-state';
import { logConsentModeError } from './google-consent-mode';
import { logConsentEvent } from './log';
import {
  CONSENT_LOG_PREFIX,
  CUSTOMERIO_WRITE_KEY,
  GA4_ID,
  GTM_ID,
  GTM_VIEW,
  MAX_QUEUED_EVENTS,
  MIDBOUND_ID,
  MIXPANEL_TOKEN,
  REDDIT_PIXEL_ID,
  RETENTION_KEY,
  TRACKING_SCRIPTS_ENABLED,
} from './constants';

/* -------------------------------------------------------------------------- */
/*                                 Pixel queue                                */
/* -------------------------------------------------------------------------- */

/**
 * Deferred marketing-pixel events.
 *
 * The lead fan-out in `src/utils/tracking.ts` guards every vendor call with a
 * bare `if (window.X)` and has no queue or retry, so a gated script means the
 * event is dropped with no trace. That was already true before consent gating —
 * an ad blocker produced the same outcome — but gating makes it systematic for
 * everyone who has not yet accepted.
 *
 * This holds those events until the relevant category is granted, so a visitor
 * who converts *and then* accepts still produces the conversion. Events for a
 * visitor who never accepts are correctly never sent.
 */
type QueuedEvent = {
  vendor: 'reddit' | 'enhanced-conversions';
  send: () => void;
};

let queue: QueuedEvent[] = [];

export function queuePixelEvent(event: QueuedEvent): void {
  if (queue.length >= MAX_QUEUED_EVENTS) {
    logConsentEvent('consent.error', {
      error: 'pixel queue overflow',
      vendor: event.vendor,
      dropped: true,
    });

    return;
  }

  queue.push(event);
}

export function flushPixelQueue(vendor: QueuedEvent['vendor']): void {
  const flushable = queue.filter((event) => event.vendor === vendor);
  queue = queue.filter((event) => event.vendor !== vendor);

  if (flushable.length === 0) {
    return;
  }

  logConsentEvent('consent.changed', {
    pixelQueueFlushed: flushable.length,
    vendors: [vendor],
  });

  for (const event of flushable) {
    try {
      event.send();
    } catch (error) {
      logConsentEvent('consent.error', {
        error: `pixel queue flush failed: ${String(error)}`,
        vendor: event.vendor,
      });
    }
  }
}

/* -------------------------------------------------------------------------- */
/*                             Script registration                            */
/* -------------------------------------------------------------------------- */

/**
 * The Retention.com ("GetEmails") loader, lifted verbatim from
 * `src/features/document/retention.tsx` so behaviour is unchanged apart from
 * the consent gate. `geq.doNotTrack` is part of the stub's method list and is
 * called from `onConsentChange` below — it was never called before.
 */
function retentionSnippet(key: string): string {
  return `!function(){var geq=window.geq=window.geq||[];if(geq.initialize)return;if(geq.invoked){if(window.console&&console.error){console.error("GE snippet included twice.");}return;}geq.invoked=true;geq.methods=["page","suppress","track","doNotTrack","trackOrder","identify","addToCart","callBack","event"];geq.factory=function(method){return function(){var args=Array.prototype.slice.call(arguments);args.unshift(method);geq.push(args);return geq;};};for(var i=0;i<geq.methods.length;i++){var k=geq.methods[i];geq[k]=geq.factory(k);}geq.load=function(key){var script=document.createElement("script");script.type="text/javascript";script.async=true;if(location.href.includes("vge=true")){script.src="https://s3-us-west-2.amazonaws.com/jsstore/a/"+key+"/ge.js?v="+Math.random();}else{script.src="https://s3-us-west-2.amazonaws.com/jsstore/a/"+key+"/ge.js";}var first=document.getElementsByTagName("script")[0];first.parentNode.insertBefore(script,first);};geq.SNIPPET_VERSION="1.6.1";geq.load("${key}");}();`;
}

/**
 * Runs a `@c15t/scripts` vendor helper, degrading a bad id to "this vendor is
 * not registered" instead of taking the page down.
 *
 * Those helpers validate their inputs and **throw** on a malformed one —
 * `mixpanelAnalytics` rejects anything that is not a 32-character hex token,
 * for instance. `buildConsentScripts` runs inside a `useMemo` during SSR, so an
 * env-var typo does not disable one tracker: it throws while rendering and
 * returns a 500 for every route. Verified locally with a short Mixpanel token.
 *
 * Failing loud-but-open is the right trade here. The alternative — letting it
 * throw — fails closed on tracking but also fails closed on the site.
 */
function registerVendor(vendor: string, build: () => Script): Script | null {
  try {
    return build();
  } catch (error) {
    console.error(
      `${CONSENT_LOG_PREFIX} ${vendor} was not registered: ${
        error instanceof Error ? error.message : String(error)
      }`,
    );

    return null;
  }
}

type BuildScriptsArgs = {
  /**
   * The first-party `tracking_id_v2` value, cross-linked into Reddit as
   * `externalId` for conversion matching. Read from the cookie by the caller.
   */
  trackingId?: string;
  /**
   * Retention.com only ran on a subset of marketing pages. Preserved because
   * it is a de-anonymisation vendor: narrower exposure is the point, and
   * widening it would be a policy change, not a refactor.
   */
  isRetentionPathname: boolean;
};

/**
 * Every vendor c15t knows about, and how each one is gated. This is the list to
 * read to answer "is X gated?".
 *
 * Three entries do not look like the rest, and none will be found by grepping
 * this file for a vendor URL:
 *
 * - **`google-consent-mode`** is not a vendor. It owns the Consent Mode v2
 *   protocol, and is registered first so its default precedes the container.
 * - **Customer.io** is here, but as a `callbackOnly` entry with no `src` — its
 *   bundle is a dynamic `import()` owned by `CustomerioAnalyticsProvider`, so
 *   c15t gates the decision and the provider does the loading.
 * - **Intercom** is not registered at all. It is deliberately ungated, like
 *   Google Maps and reCAPTCHA: it is Belong's communications platform rather
 *   than a tracking vendor, and we send it no events and no behavioural
 *   attributes. It loads through `next/script` in
 *   `src/features/document/components/intercom/intercom.tsx`, which is where
 *   that reasoning is written down.
 */
export function buildConsentScripts({
  trackingId,
  isRetentionPathname,
}: BuildScriptsArgs): Script[] {
  const scripts: Script[] = [];

  // --- Always loaded, gated by the vendor's own consent API -----------------

  scripts.push(
    ...buildGoogleConsentScripts({
      gtmId: GTM_ID,
      gtmView: GTM_VIEW,
      ga4Id: GA4_ID,
      getModel: () => getConsentSnapshot()?.model,
      onError: logConsentModeError,
    }),
  );

  if (MIXPANEL_TOKEN) {
    const mixpanelToken = MIXPANEL_TOKEN;

    // `alwaysLoad` + Mixpanel's own `opt_in_tracking`/`opt_out_tracking`, which
    // c15t calls on consent transitions. Keeping the SDK loaded matters here:
    // `src/utils/tracking.ts` guards every call with `if (window.mixpanel)`,
    // so an absent SDK silently drops `lead_created` instead of suppressing it.
    //
    // `scriptUrl` and `api_host` preserve the existing first-party proxy
    // (`next.config.js` rewrites `/mp-min.js` and `/mp/*`), which is what keeps
    // Mixpanel working behind ad blockers.
    const mixpanelScript = registerVendor('Mixpanel', () =>
      mixpanelAnalytics({
        token: mixpanelToken,
        scriptUrl: '/mp-min.js',
        initOptions: {
          api_host: '/mp',
          track_pageview: true,
          persistence: 'localStorage',
          record_mask_text_class: 'input[type="password"]',
          // Session replay starts at 0% and is turned on explicitly below once
          // `measurement` is granted. Replay is a different risk from event
          // analytics and is what session-replay wiretap claims target.
          record_sessions_percent: 0,
          // Opt out until c15t says otherwise. Non-prod previously set
          // `ignore_dnt: true` and force-called `opt_in_tracking()`, which is
          // the opposite of what we now want in any environment.
          opt_out_tracking_by_default: true,
        },
      }),
    );

    if (mixpanelScript) {
      scripts.push({
        ...mixpanelScript,
        onConsentChange: (info) => {
          // Compose rather than replace: the helper's own handler is what calls
          // `opt_in_tracking`/`opt_out_tracking`, and dropping it would leave
          // event collection ungated.
          mixpanelScript.onConsentChange?.(info);

          // Replay stays production-only, matching the previous
          // `shouldRecord = env === 'production'` behaviour — consent is now an
          // additional condition, not a replacement for it.
          if (!isProd) {
            return;
          }

          if (info.hasConsent) {
            window.mixpanel?.start_session_recording?.();
          } else {
            window.mixpanel?.stop_session_recording?.();
          }
        },
      });
    }
  }

  // --- Hard gated: nothing loads until the category is granted --------------

  if (!TRACKING_SCRIPTS_ENABLED) {
    return scripts;
  }

  if (REDDIT_PIXEL_ID) {
    const redditPixelId = REDDIT_PIXEL_ID;

    const redditScript = registerVendor('Reddit pixel', () =>
      redditPixel({
        pixelId: redditPixelId,
        initOptions: trackingId ? { externalId: trackingId } : undefined,
      }),
    );

    if (redditScript) {
      scripts.push(redditScript);
    }
  }

  if (RETENTION_KEY && isRetentionPathname) {
    scripts.push({
      id: 'retention',
      textContent: retentionSnippet(RETENTION_KEY),
      category: 'marketing',
      onConsentChange: ({ hasConsent }) => {
        // The stub exposes `doNotTrack` but nothing ever called it. Once the
        // bundle is in the page we cannot unload it, so this is the only way to
        // honour a revocation.
        if (!hasConsent) {
          window.geq?.doNotTrack?.();
        }
      },
    });
  }

  if (MIDBOUND_ID) {
    scripts.push({
      id: 'midbound',
      src: `https://p.midbound.click/${MIDBOUND_ID}`,
      category: 'marketing',
    });
  }

  if (CUSTOMERIO_WRITE_KEY) {
    // Registered as callback-only: the Customer.io CDP bundle is a dynamic
    // `import()` owned by `CustomerioAnalyticsProvider`, not a `<script src>`,
    // so c15t gates the *decision* and the provider does the loading.
    scripts.push({
      id: 'customerio-cdp',
      callbackOnly: true,
      category: 'measurement',
    });
  }

  return scripts;
}
